import { useTranslation } from "react-i18next";
import { ExternalLink, Smartphone, ArrowRight, Send, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { StepCard } from "./StepCard";
import { SendTokenForm } from "./SendTokenForm";

interface OffRampP2PProps {
  currencyCode: string;
  countryName: string;
}

/**
 * Generic P2P off-ramp guide for countries where El Dorado
 * doesn't support the local currency (Chile, Mexico).
 * Uses Binance P2P as the primary method.
 */
function OffRampP2P({ currencyCode, countryName }: OffRampP2PProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-gradient-to-r from-brand-500/10 to-success-500/10 p-4">
        <p
          className="text-sm text-text-secondary"
          dangerouslySetInnerHTML={{
            __html: t("ramp.p2pIntro", {
              currency: currencyCode,
              country: countryName,
            }),
          }}
        />
      </div>

      <div className="space-y-5">
        <StepCard
          step={1}
          title={t("ramp.step1Binance")}
          description={t("ramp.step1BinanceDesc")}
          icon={<Smartphone className="h-4 w-4 text-brand-500" />}
          action={
            <div className="flex flex-wrap gap-2">
              <a
                href={`https://p2p.binance.com/trade/sell/USDT?fiat=${currencyCode}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="secondary" size="sm" className="gap-1.5">
                  Binance P2P
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </a>
            </div>
          }
        />

        <StepCard
          step={2}
          title={t("ramp.step2BinanceAddress")}
          description={t("ramp.step2BinanceAddressDesc")}
          icon={<ArrowRight className="h-4 w-4 text-brand-500" />}
        />

        <StepCard
          step={3}
          title={t("ramp.step3Send")}
          description={t("ramp.step3SendDesc")}
          icon={<Send className="h-4 w-4 text-brand-500" />}
          action={<SendTokenForm title={t("ramp.sendToBinance")} />}
        />

        <StepCard
          step={4}
          title={t("ramp.step4SellP2P", { currency: currencyCode })}
          description={t("ramp.step4SellP2PDesc", { currency: currencyCode })}
          icon={<ArrowRight className="h-4 w-4 text-brand-500" />}
        />
      </div>

      <div className="flex gap-2 rounded-xl border border-warning-200 bg-warning-50 p-3 dark:border-warning-800 dark:bg-warning-950">
        <AlertTriangle className="h-4 w-4 shrink-0 text-warning-500" />
        <p
          className="text-xs text-warning-600 dark:text-warning-500"
          dangerouslySetInnerHTML={{ __html: t("ramp.warningPolygonShort") }}
        />
      </div>
    </div>
  );
}

export { OffRampP2P };
